import { Link } from 'react-router-dom';

const LAST_UPDATED = 'May 2026';

const sections = [
  {
    title: '1. Acceptance of Terms',
    body: `By accessing or using SublingoX ("the App"), you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use the App.`,
  },
  {
    title: '2. Description of Service',
    body: `SublingoX is a language learning platform that provides vocabulary building, conversation practice, alphabet guides, and grammar foundations across multiple languages. The service is provided for personal, non-commercial educational use.`,
  },
  {
    title: '3. User Accounts',
    body: `You may use the App as a guest or by creating an account. You are responsible for maintaining the confidentiality of your account credentials. You agree to provide accurate information and to notify us of any unauthorised use of your account. Guest preferences are stored locally on your device and are not synced across devices.`,
  },
  {
    title: '4. Acceptable Use',
    body: `You agree not to misuse the App. Prohibited activities include attempting to reverse engineer the App, scraping content for commercial use, using the App to distribute harmful or illegal content, or circumventing any security features.`,
  },
  {
    title: '5. Intellectual Property',
    body: `The SublingoX interface, design, and proprietary educational materials are owned by or licensed to SublingoX. You may not copy, reproduce, or redistribute these without prior written permission. Language data files are provided for in-app educational use only. Certain third-party content is used under open licences as described in Section 6 below.`,
  },
  {
    title: '6. Third-Party Content & Attribution',
    body: `Some audio recordings available in the "Real Voice Accent Practice" feature are sourced from Wikimedia Commons files released under the Creative Commons Zero 1.0 Universal public domain dedication (CC0-1.0). Older local tracks may also be sourced from LinguaLibre.org and made available under the Creative Commons Attribution-ShareAlike 4.0 International licence (CC BY-SA 4.0). SublingoX credits the source metadata where available and does not claim ownership of these third-party recordings.`,
  },
  {
    title: '7. Text-to-Speech and Speech Recognition',
    body: `The App uses speech services provided by your browser or operating system for text-to-speech and optional speech-to-text practice. Microphone audio may be processed by that browser, operating system, or its speech provider under their respective terms. SublingoX receives the recognized text only to calculate an on-device practice score and does not store the recording or transcript.`,
  },
  {
    title: '8. Disclaimer of Warranties',
    body: `The App is provided "as is" without warranties of any kind. We do not guarantee that all language content is 100% accurate or complete, that the service will be uninterrupted or error-free, or that content is suitable for formal examination preparation.`,
  },
  {
    title: '9. Limitation of Liability',
    body: `To the fullest extent permitted by law, SublingoX and its creators shall not be liable for any indirect, incidental, or consequential damages arising from your use of the App, including but not limited to loss of data or educational outcomes.`,
  },
  {
    title: '10. Changes to Terms',
    body: `We reserve the right to update these Terms at any time. Continued use of the App after any changes constitutes your acceptance of the updated Terms. We will update the "Last updated" date above when changes are made.`,
  },
  {
    title: '11. Contact',
    body: `If you have questions about these Terms, please contact us through the App's feedback channels or at the email address associated with your account.`,
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12">
      <div className="mx-auto max-w-3xl">

        {/* Header */}
        <div className="mb-10">
          <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-4xl font-black text-slate-900">Terms &amp; Conditions</h1>
          <p className="mt-2 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>
          <p className="mt-4 text-slate-600">
            Please read these Terms and Conditions carefully before using SublingoX. These terms govern your access to and use of our language learning platform.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((s) => (
            <section key={s.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-3 text-lg font-bold text-slate-800">{s.title}</h2>
              <p className="text-sm leading-relaxed text-slate-600">{s.body}</p>
            </section>
          ))}
        </div>

        {/* Footer nav */}
        <div className="mt-10 flex flex-col items-center gap-2 text-center text-xs text-slate-400">
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-indigo-600 hover:underline">Privacy Policy</Link>
            <Link to="/credits" className="hover:text-indigo-600 hover:underline">Credits</Link>
            <Link to="/" className="hover:text-indigo-600 hover:underline">Home</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} SublingoX · Subram Miles</p>
        </div>
      </div>
    </div>
  );
}
